<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-tp-top10">
    <el-form :inline="true" class="kb-filter-bar" size="small" @submit.native.prevent>
      <el-form-item label="统计年度">
        <el-select v-model="year" style="width: 110px" @change="loadData">
          <el-option v-for="y in yearOptions" :key="y" :label="y + ' 年'" :value="String(y)" />
        </el-select>
      </el-form-item>
      <el-form-item label="排行指标">
        <el-select v-model="metric" style="width: 200px" @change="loadData">
          <el-option label="计划数量（吨）" value="plan" />
          <el-option label="计划执行进度（吨）" value="exec" />
          <el-option label="完成率（%）" value="rate" />
        </el-select>
      </el-form-item>
      <el-form-item label="业务类型">
        <el-select v-model="businessType" clearable placeholder="全部" style="width: 120px" @change="loadData">
          <el-option label="铁运入库" :value="1" />
          <el-option label="汽运出库" :value="2" />
          <el-option label="汽运入库" :value="3" />
        </el-select>
      </el-form-item>
      <el-form-item label="自动刷新">
        <el-switch v-model="autoRefresh" active-text="30s" @change="onAutoRefreshChange" />
      </el-form-item>
    </el-form>

    <div class="layout">
      <div class="chart-card">
        <h3>TOP10 排行榜（{{ metricLabel }}）</h3>
        <div ref="chartRef" class="chart-box" />
      </div>
      <div class="side">
        <div class="podium">
          <h4>前三名</h4>
          <template v-if="topThree.length">
            <div v-for="(row, idx) in topThree" :key="idx" :class="'medal r' + (idx + 1)">
              <div class="rk">{{ ['🥇', '🥈', '🥉'][idx] }}</div>
              <div style="flex:1;min-width:0">
                <div class="nm" :title="row.custName">{{ row.custName }}</div>
                <div class="sub">
                  {{ rankLine(row) }} · 完成率
                  {{ row.completionRatePercent != null ? row.completionRatePercent.toFixed(1) + '%' : '—' }}
                </div>
              </div>
            </div>
          </template>
          <div v-else style="color:#94a3b8;font-size:13px">暂无数据</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchTransportPlanTop10Customers } from './api'

export default {
  name: 'KanbanTransportPlanTop10Customers',
  components: { NxReportHeader },
  data() {
    const cy = new Date().getFullYear()
    return {
      loading: false,
      year: String(cy),
      metric: 'plan',
      businessType: null,
      result: { items: [] },
      chartInstance: null,
      autoRefresh: false,
      refreshTimer: null
    }
  },
  computed: {
    yearOptions() {
      const cy = new Date().getFullYear()
      const list = []
      for (let y = cy + 1; y >= cy - 5; y--) list.push(y)
      return list
    },
    items() {
      return this.result.items || []
    },
    topThree() {
      return this.items.slice(0, 3)
    },
    metricLabel() {
      if (this.metric === 'exec') return '执行量'
      if (this.metric === 'rate') return '完成率'
      return '计划量'
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.clearAutoRefresh()
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },
  methods: {
    rankLine(row) {
      if (this.metric === 'rate') {
        return (row.rankValue != null ? row.rankValue.toFixed(1) : '—') + '%'
      }
      return (row.rankValue != null ? row.rankValue.toLocaleString('zh-CN', { maximumFractionDigits: 2 }) : '—') + ' 吨'
    },
    onResize() {
      this.chartInstance && this.chartInstance.resize()
    },
    onAutoRefreshChange(on) {
      this.clearAutoRefresh()
      if (on) {
        this.refreshTimer = setInterval(() => this.loadData(), 30000)
      }
    },
    clearAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    barColor(i) {
      if (i === 0) {
        return new echarts.graphic.LinearGradient(0, 0, 1, 0, [
          { offset: 0, color: '#b45309' },
          { offset: 1, color: '#fbbf24' }
        ])
      }
      if (i === 1) {
        return new echarts.graphic.LinearGradient(0, 0, 1, 0, [
          { offset: 0, color: '#475569' },
          { offset: 1, color: '#94a3b8' }
        ])
      }
      if (i === 2) {
        return new echarts.graphic.LinearGradient(0, 0, 1, 0, [
          { offset: 0, color: '#c2410c' },
          { offset: 1, color: '#fb923c' }
        ])
      }
      const blues = ['#0369a1', '#0284c7', '#0ea5e9', '#38bdf8', '#7dd3fc', '#bae6fd', '#38bdf8']
      return blues[i % blues.length]
    },
    async loadData() {
      this.loading = true
      try {
        const data = await fetchTransportPlanTop10Customers({
          year: parseInt(this.year, 10),
          businessType: this.businessType,
          metric: this.metric
        })
        this.result = data
        this.$nextTick(() => this.renderChart())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    renderChart() {
      const el = this.$refs.chartRef
      if (!el) return
      const list = this.items
      if (!this.chartInstance) this.chartInstance = echarts.init(el)
      const isRate = this.metric === 'rate'
      const names = list.map((r) => r.custName)
      const vals = list.map((r) => r.rankValue || 0)
      if (!list.length) {
        this.chartInstance.setOption({
          title: { text: '暂无数据', left: 'center', top: 'middle', textStyle: { color: '#94a3b8', fontSize: 14 } },
          xAxis: { show: false },
          yAxis: { show: false },
          series: []
        })
        return
      }
      this.chartInstance.setOption({
        title: { show: false },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const p = params[0]
            const row = list[p.dataIndex]
            let s = row.custName + '<br/>'
            s += '计划：' + row.planTotal.toLocaleString('zh-CN', { maximumFractionDigits: 2 }) + ' 吨<br/>'
            s += '执行：' + row.executeTotal.toLocaleString('zh-CN', { maximumFractionDigits: 2 }) + ' 吨<br/>'
            if (row.completionRatePercent != null) {
              s += '完成率：' + row.completionRatePercent.toFixed(2) + '%<br/>'
            }
            if (this.metric === 'plan') {
              s += '<b>当前排名：第' + (p.dataIndex + 1) + '名</b>'
            } else {
              s +=
                '<b>' +
                this.metricLabel +
                '：' +
                (isRate ? (row.rankValue != null ? row.rankValue.toFixed(2) : '—') + '%' : (row.rankValue || 0).toLocaleString() + ' 吨') +
                '</b>'
            }
            return s
          }
        },
        grid: { left: 8, right: 48, top: 12, bottom: 8, containLabel: true },
        xAxis: {
          type: 'value',
          max: (v) => Math.ceil(v.max * 1.06),
          splitLine: { lineStyle: { type: 'dashed', color: '#e2e8f0' } },
          axisLabel: {
            formatter: (v) => (isRate ? v + '%' : v >= 10000 ? (v / 10000).toFixed(1) + '万' : v),
            color: '#64748b',
            fontSize: 11
          }
        },
        yAxis: {
          type: 'category',
          data: names,
          inverse: true,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#334155', fontSize: 12, width: 100, overflow: 'truncate' }
        },
        series: [
          {
            type: 'bar',
            realtimeSort: true,
            data: vals.map((v, i) => ({
              value: v,
              itemStyle: { color: this.barColor(i), borderRadius: [0, 8, 8, 0] }
            })),
            barWidth: '55%',
            label: {
              show: true,
              position: 'right',
              formatter: (p) =>
                isRate ? (p.value != null ? Number(p.value).toFixed(1) : '') + '%' : Number(p.value).toLocaleString(),
              color: '#475569',
              fontSize: 11,
              fontWeight: 600
            }
          }
        ],
        animationDurationUpdate: 600
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
